import { ref } from 'vue'
import store from '@/store'
import { config } from '@/config.js'
import { useToast } from '@/composables/common/useToast'

/**
 * Catálogo y alta de etiquetas de bloqueo.
 *
 * Vive en un composable y no en el selector porque hace peticiones, mantiene
 * estado de carga y muestra feedback: responsabilidades de infraestructura que no
 * pertenecen a una pieza de presentación. `EtiquetaSelect` queda como selector
 * controlado que renderiza opciones y emite la selección.
 *
 * @returns {{
 *   etiquetas: import('vue').Ref<Array>,
 *   loading: import('vue').Ref<boolean>,
 *   loadEtiquetas: () => Promise<Array|null>,
 *   createEtiqueta: (data: Object) => Promise<Object|null>
 * }}
 */
export function useEtiquetas() {
    // `config` del módulo, no `getCurrentInstance().proxy.config`: el helper de
    // rutas debe estar disponible aunque el composable se invoque fuera de un
    // contexto de componente (o con el getter del store sin hidratar).
    const toast = useToast()

    const etiquetas = ref([])
    const loading = ref(false)

    /**
     * Normaliza la respuesta del catálogo a un array.
     *
     * El endpoint devuelve `{ data: [...] }`, pero la forma no está garantizada
     * en el código: otro consumidor del mismo endpoint lee `res.data` plano. Si
     * llegara esa variante, asignar `res.data.data` guardaría `undefined` y el
     * selector quedaría vacío en silencio, sin error ni toast.
     *
     * @param {Object} res respuesta de la acción Vuex.
     * @returns {Array}
     */
    function normalizeEtiquetas(res) {
        const candidato = res?.data?.data ?? res?.data ?? [];

        return Array.isArray(candidato) ? candidato : [];
    }

    /**
     * Carga el catálogo de etiquetas seleccionables del teatro activo.
     * @returns {Promise<Array|null>} la lista cargada, o `null` si falló.
     */
    async function loadEtiquetas() {
        loading.value = true

        try {
            const res = await store.dispatch(config.getVuexAlias('back.teatro.etiquetas.index'), {
                filters: { to_select: true },
                teatro_id: store.getters.getTeatro.id
            })

            etiquetas.value = normalizeEtiquetas(res)
            return etiquetas.value
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al cargar etiquetas')
            return null
        } finally {
            loading.value = false
        }
    }

    /**
     * Crea una etiqueta y devuelve la creada.
     *
     * Éxito y error se distinguen y se propagan: quien llama (la vista o un test)
     * necesita saber si la etiqueta llegó a existir. La recarga del catálogo se
     * hace UNA sola vez, aquí, y no también desde el cierre del modal: dos
     * peticiones concurrentes podían sobrescribir la lista en orden no
     * determinista.
     *
     * @param {Object} etiquetaData
     * @returns {Promise<Object|null>} la etiqueta creada, o `null` si el alta tuvo
     *   éxito pero su id no se pudo resolver desde el catálogo.
     * @throws {Error} si el alta falla.
     */
    async function createEtiqueta(etiquetaData) {
        const payload = { ...etiquetaData, teatro_id: store.getters.getTeatro.id }
        const nombre = payload.name

        const res = await store.dispatch(config.getVuexAlias('back.teatro.etiquetas.crear'), payload)
        toast.success(res.data.message)

        // El endpoint de alta sólo devuelve un mensaje, así que el id se resuelve
        // desde el catálogo recién recargado. Se devuelve null en vez de lanzar
        // cuando no aparece: la etiqueta ya existe y quien llama necesita su
        // nombre para poder limpiarla igualmente.
        const lista = await loadEtiquetas()

        return (lista || []).find(etiqueta => etiqueta.name === nombre) || null
    }

    return { etiquetas, loading, loadEtiquetas, createEtiqueta }
}
